import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, KeyRound, Loader2, Users, Sparkles } from "lucide-react";

const IMG = "https://images.pexels.com/photos/6949525/pexels-photo-6949525.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
    const { login, error, setError } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [demoAccounts, setDemoAccounts] = useState([]);
    const [demoPassword, setDemoPassword] = useState("Demo2026!");
    const [showDemo, setShowDemo] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/auth/demo-accounts").then(({ data }) => {
            setDemoAccounts(data.accounts || []);
            setDemoPassword(data.password || "Demo2026!");
        }).catch(() => {});
    }, []);

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setBusy(true);
        const ok = await login(email.trim(), password);
        setBusy(false);
        if (ok) navigate("/");
    }

    function fillDemo(acct) {
        setEmail(acct.email);
        setPassword(demoPassword);
        setShowDemo(false);
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left / brand */}
            <div className="relative hidden lg:block">
                <img src={IMG} alt="Kamoto executive boardroom" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(10,17,40,0.95), rgba(13,16,23,0.72))" }} />
                <div className="relative h-full flex flex-col justify-between p-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg, #b87333, #2d68c4)" }}>
                            <span className="font-display font-bold text-white text-lg">K</span>
                        </div>
                        <div className="font-display text-xl font-semibold">KCC<span className="text-kcc-copper-light">360</span><sup className="text-[10px] text-kcc-silver ml-0.5">™</sup></div>
                    </div>

                    <div className="max-w-lg">
                        <div className="kcc-label text-kcc-copper-light">Kamoto Copper Company</div>
                        <h1 className="font-display text-4xl xl:text-5xl font-semibold tracking-tight mt-3 leading-tight">
                            The Integrated Digital Mining Operating System
                        </h1>
                        <p className="kcc-text-silver mt-4 text-base leading-relaxed">
                            One secure operational picture — from open pit and underground, through the concentrator and refinery,
                            all the way to the Board room. Governed data. Governed AI. Zero-trust security.
                        </p>
                        <div className="mt-6 grid grid-cols-3 gap-3 text-xs">
                            <div className="kcc-surface p-3">
                                <div className="kcc-label">Modules</div>
                                <div className="font-display text-xl mt-1">20+</div>
                            </div>
                            <div className="kcc-surface p-3">
                                <div className="kcc-label">Roles</div>
                                <div className="font-display text-xl mt-1">50+</div>
                            </div>
                            <div className="kcc-surface p-3">
                                <div className="kcc-label">Data horizon</div>
                                <div className="font-display text-xl mt-1">24-36m</div>
                            </div>
                        </div>
                    </div>

                    <div className="text-[11px] kcc-text-dim flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 text-kcc-green" />
                        Zero-Trust · MFA-Ready · Audit-Enabled · SAML/OIDC-Compatible
                    </div>
                </div>
            </div>

            {/* Right / login form */}
            <div className="flex flex-col justify-center px-6 sm:px-12 py-10 bg-[#0d1017]">
                <div className="w-full max-w-md mx-auto">
                    <div className="lg:hidden flex items-center gap-3 mb-8">
                        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg, #b87333, #2d68c4)" }}>
                            <span className="font-display font-bold text-white text-lg">K</span>
                        </div>
                        <div className="font-display text-xl font-semibold">KCC<span className="text-kcc-copper-light">360</span></div>
                    </div>

                    <div className="kcc-label text-kcc-copper-light">Enterprise Sign-In</div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight mt-2 text-white">Welcome back</h2>
                    <p className="kcc-text-silver text-sm mt-2">
                        Use your KCC enterprise credentials, or explore the platform as a demonstration role.
                    </p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
                        <div>
                            <label className="kcc-label block mb-1.5">Corporate email</label>
                            <input
                                type="email" required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="firstname.lastname@kcc.demo"
                                className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2.5 text-sm outline-none focus:border-kcc-copper/60"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <label className="kcc-label block mb-1.5">Password</label>
                            <div className="relative">
                                <KeyRound className="w-4 h-4 absolute left-3 top-3 kcc-text-silver" />
                                <input
                                    type="password" required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full bg-white/[0.03] border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-sm outline-none focus:border-kcc-copper/60"
                                    data-testid="login-password"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="text-sm text-kcc-red bg-kcc-red/10 border border-kcc-red/30 rounded-md px-3 py-2" data-testid="login-error">{error}</div>
                        )}

                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full bg-kcc-copper hover:bg-kcc-copper-light text-white rounded-md py-2.5 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                            data-testid="login-submit"
                        >
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in securely
                        </button>

                        <button
                            type="button"
                            onClick={() => setShowDemo((v) => !v)}
                            className="w-full bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 text-white rounded-md py-2.5 font-medium transition-colors flex items-center justify-center gap-2"
                            data-testid="show-demo"
                        >
                            <Users className="w-4 h-4 text-kcc-cobalt-light" /> Explore as a demonstration role
                        </button>
                    </form>

                    {showDemo && (
                        <div className="mt-4 kcc-surface p-3 max-h-80 overflow-y-auto animate-fade-in" data-testid="demo-panel">
                            <div className="kcc-label px-1 pb-2 flex items-center gap-2">
                                <Sparkles className="w-3 h-3 text-kcc-purple" /> Demo password: <span className="font-mono-kcc text-white/80">{demoPassword}</span>
                            </div>
                            {demoAccounts.map((a) => (
                                <button
                                    key={a.email}
                                    onClick={() => fillDemo(a)}
                                    className="w-full text-left px-2.5 py-2 rounded hover:bg-white/5 flex items-center gap-3 group"
                                    data-testid={`demo-${a.role}`}
                                >
                                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10.5px] font-display font-semibold" style={{ background: "linear-gradient(135deg, #b87333, #2d68c4)" }}>
                                        {(a.name || "?").split(" ").map(x => x[0]).slice(0,2).join("")}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-white truncate">{a.title || a.role}</div>
                                        <div className="text-[10.5px] kcc-text-silver truncate">{a.email}</div>
                                    </div>
                                    <div className="text-[10px] kcc-text-dim uppercase tracking-wider group-hover:text-kcc-copper-light">Use</div>
                                </button>
                            ))}
                        </div>
                    )}

                    <p className="mt-8 text-[11px] kcc-text-dim text-center">
                        Fictional demonstration environment. Not connected to production KCC systems.
                    </p>
                </div>
            </div>
        </div>
    );
}
